import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { getApiErrorMessage } from '@/services/api'
import { useDeleteContract } from '@/hooks/useContracts'
import type { Contract } from '@/types'

interface Props {
  contract: Contract | null
  onOpenChange: (open: boolean) => void
  onDeleted?: () => void
  // When set, navigate to the contract list after a successful delete.
  redirectToList?: boolean
}

// 2.8.11 Delete contract — Figma 1336:31123.
export function DeleteContractDialog({
  contract,
  onOpenChange,
  onDeleted,
  redirectToList,
}: Props) {
  const { t } = useTranslation('contracts')
  const navigate = useNavigate()
  const del = useDeleteContract()

  const confirm = async () => {
    if (!contract) return
    // Only Draft or Declined contracts are deletable (2.8.11-012).
    if (contract.status !== 'draft' && contract.status !== 'declined') {
      toast({ variant: 'destructive', title: t('delete.cannotDelete') })
      onOpenChange(false)
      return
    }
    try {
      await del.mutateAsync(contract.id)
      toast({ variant: 'success', title: t('delete.success') })
      onDeleted?.()
      onOpenChange(false)
      if (redirectToList) navigate('/contracts')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('delete.error')),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!contract}
      onOpenChange={onOpenChange}
      title={t('delete.title')}
      description={t('delete.confirmMessage')}
      confirmLabel={t('delete.confirm')}
      onConfirm={confirm}
      busy={del.isPending}
    />
  )
}

export default DeleteContractDialog
